import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../services/apiClient';
import { sequenceService } from '../services/sequenceService';
import type { OutreachSequence, SequenceWritePayload } from '../types/sequence';
import { useToast } from '../contexts/ToastContext';
import { DataListTable, type Column } from '../components/crm/DataListTable';
import { ListPageHeader } from '../components/crm/ListPageHeader';
import { RowActionsMenu } from '../components/crm/RowActionsMenu';
import { channelMixSummary } from '../components/sequences/copy';

function presetToPayload(preset: OutreachSequence): SequenceWritePayload {
  return {
    name: preset.name,
    description: preset.description,
    vertical_key: preset.vertical_key,
    send_window: preset.send_window,
    max_defers: preset.max_defers,
    steps: preset.steps.map((s) => ({
      step_index: s.step_index,
      channel: s.channel,
      delay_business_days: s.delay_business_days,
      template: s.template,
      task_title: s.task_title,
      is_final: s.is_final,
    })),
  };
}

const Sequences: React.FC = () => {
  const [sequences, setSequences] = useState<OutreachSequence[]>([]);
  const [presets, setPresets] = useState<OutreachSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [adopting, setAdopting] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [seqs, pre] = await Promise.all([
        sequenceService.listSequences(),
        sequenceService.listPresets(),
      ]);
      setSequences(seqs);
      setPresets(pre);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Failed to load sequences.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const adoptPreset = useCallback(
    async (preset: OutreachSequence) => {
      setAdopting(true);
      try {
        const created = await sequenceService.createSequence(presetToPayload(preset));
        showToast('Sequence created', 'success');
        navigate(`/sequences/${created.id}`);
      } catch (err) {
        showToast(err instanceof ApiError ? err.message : 'Could not create the sequence.', 'error');
      } finally {
        setAdopting(false);
      }
    },
    [navigate, showToast]
  );

  const removeSequence = useCallback(
    async (sequence: OutreachSequence) => {
      if (!window.confirm(`Delete "${sequence.name}"? This cannot be undone.`)) return;
      try {
        await sequenceService.deleteSequence(sequence.id);
        showToast('Sequence deleted', 'success');
        setSequences((prev) => prev.filter((s) => s.id !== sequence.id));
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          showToast('This sequence has people in it. Stop their sequences first.', 'error');
        } else {
          showToast(err instanceof ApiError ? err.message : 'Could not delete the sequence.', 'error');
        }
      }
    },
    [showToast]
  );

  const columns: Column<OutreachSequence>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Sequence',
        mobilePriority: 'primary',
        mobileLabel: 'Sequence',
        render: (s) => (
          <Link to={`/sequences/${s.id}`} className="font-medium text-primary hover:underline">
            {s.name}
          </Link>
        ),
      },
      {
        id: 'steps',
        header: 'Steps',
        mobileLabel: 'Steps',
        render: (s) => channelMixSummary(s.steps) || `${s.step_count} steps`,
      },
      {
        id: 'span',
        header: 'Span',
        mobileLabel: 'Span',
        render: (s) => `${s.business_day_span} business days`,
      },
      {
        id: 'people',
        header: 'People in it',
        mobileLabel: 'People in it',
        render: (s) => s.active_enrollment_count,
      },
      {
        id: 'edited',
        header: 'Last edited',
        mobileLabel: 'Last edited',
        render: (s) => new Date(s.updated_at).toLocaleDateString(),
      },
      {
        id: 'actions',
        header: '',
        render: (s) => (
          <RowActionsMenu
            items={[
              { id: 'edit', label: 'Edit', onClick: () => navigate(`/sequences/${s.id}`) },
              { id: 'delete', label: 'Delete', onClick: () => removeSequence(s) },
            ]}
          />
        ),
      },
    ],
    [navigate, removeSequence]
  );

  const emptyState = (
    <div className="rounded-xl border border-border bg-surface p-8 text-center">
      <p className="mx-auto max-w-md text-sm text-fg-muted">
        Sequences send a planned series of touches and stop the moment someone replies.
      </p>
      <div className="mt-4">
        <Link to="/sequences/new" className="btn-primary rounded-xl px-4 py-2 text-sm">
          Create a sequence
        </Link>
      </div>
      {presets.length > 0 ? (
        <div className="mt-8 text-left">
          <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-fg-muted">
            Or start from a preset
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                disabled={adopting}
                onClick={() => adoptPreset(preset)}
                className="rounded-xl border border-border bg-surface p-4 text-left transition hover:border-primary disabled:opacity-50"
              >
                <div className="font-medium text-fg">{preset.name}</div>
                <div className="mt-1 text-xs text-fg-muted">{channelMixSummary(preset.steps)}</div>
                {preset.description ? (
                  <div className="mt-2 text-xs text-fg-muted">{preset.description}</div>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Sequences"
        subtitle="Planned multi-touch outreach that stops the moment someone replies."
        actions={
          <Link to="/sequences/new" className="btn-primary rounded-xl px-4 py-2 text-sm">
            Create a sequence
          </Link>
        }
      />

      {!loading && sequences.length === 0 ? (
        emptyState
      ) : (
        <DataListTable
          columns={columns}
          rows={sequences}
          getRowKey={(s) => s.id}
          loading={loading}
          emptyContent={emptyState}
        />
      )}
    </div>
  );
};

export default Sequences;
